import { Popover, Transition } from '@headlessui/react'
import { BUTTON_SIZE, BUTTON_TYPE, DSButton } from '@repo/design-system/index'
import { QIcon_chevron_down, QIcon_chevron_up } from '@repo/icons/index'
import classNames from 'classnames'
import { Fragment, useState } from 'react'
import TextareaAutosize from 'react-textarea-autosize'
import { toast } from 'react-toastify'
import { HACKLE_FLAG_KEY, useFeatureFlag } from '../../../../helpers/hackleHelper'
import apis from '../../../../modules/apis'
import { axiosGet, axiosPost } from '../../../../modules/axiosHelper'
import { APP_SCREEN_NAME } from '../../../../modules/hooks/common/appRouterVars'
import useAppRouter from '../../../../modules/hooks/common/useAppRouter'
import useIsWebview from '../../../../modules/hooks/common/useIsWebview'
import useInAppReview from '../../../../modules/hooks/webview/common/useInAppReview'
import useReturnFullUrl from '../../../../modules/hooks/webview/common/useReturnFullUrl'
import { n_common, n_review, s_review } from '../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../modules/i18n/useTranslation'
import {
  LESSON_EVALUATION_TUTOR_BAD_GRAY,
  LESSON_EVALUATION_TUTOR_BAD_PRIMARY,
  LESSON_EVALUATION_TUTOR_GOOD_GRAY,
  LESSON_EVALUATION_TUTOR_GOOD_PRIMARY,
  LESSON_EVALUATION_TUTOR_SOSO_GRAY,
  LESSON_EVALUATION_TUTOR_SOSO_PRIMARY,
} from '../../../../modules/images'
import urls from '../../../../modules/urls'
import useEvaluationStore from '../../../../store/useEvaluationStore'
import { useConfirmPopup } from '../../../basic/popup/ConfirmPopup'
import LoadingSpinner from '../../../common/LoadingSpinner'
import { Tutor } from './EvaluationModalBody'
import { SelectPreferenceSection } from './EvaluationStep3'

interface IPreferList {
  status: string
  beforeImg: string
  afterImg: string
  value: number
}

const preferList: IPreferList[] = [
  {
    status: 'bad',
    beforeImg: LESSON_EVALUATION_TUTOR_BAD_GRAY,
    afterImg: LESSON_EVALUATION_TUTOR_BAD_PRIMARY,
    value: 1,
  },
  {
    status: 'soso',
    beforeImg: LESSON_EVALUATION_TUTOR_SOSO_GRAY,
    afterImg: LESSON_EVALUATION_TUTOR_SOSO_PRIMARY,
    value: 2,
  },
  {
    status: 'good',
    beforeImg: LESSON_EVALUATION_TUTOR_GOOD_GRAY,
    afterImg: LESSON_EVALUATION_TUTOR_GOOD_PRIMARY,
    value: 3,
  },
]

const DISCLOSURE = {
  ALL: 0,
  TEAM: 1,
  TEAM_AND_TUTOR: 2,
}

type PostDataPayload = {
  answer: {
    1: number // evaluationRate
    2: number // materialPreference
    4: number // networkPreference
  }
  answer_comment: {
    1: string // detailReviewDesc
    2: string // materialReviewDesc
    4: string // networkReviewDesc
  }
  evaluation_keyword_ids: any[]
  public_option: number // detailReviewSelect
  preferred_level: number // tutorPreference
}

interface ILessonEvaluationStep4 {
  lessonId: number
  isRookie: boolean
  pointBackAmount?: number
  refetch?: () => void | null
  sendClickEventToGTM: (buttonName: string) => void
  onClose: () => void
  tutorData: Tutor
  course: any
}

const EvaluationStep4 = ({
  lessonId,
  isRookie,
  pointBackAmount,
  refetch,
  sendClickEventToGTM,
  tutorData,
  onClose,
  course,
}: ILessonEvaluationStep4) => {
  const { t } = useTranslation()
  const router = useAppRouter()
  const isWebview = useIsWebview()
  const confirmPopup = useConfirmPopup()
  const [loading, setLoading] = useState<boolean>(false)
  const { tutorPreference, keywordsList, evaluationRate, setInitialState } = useEvaluationStore((state) => ({
    tutorPreference: state.tutorPreference,
    keywordsList: state.keywordsList,
    evaluationRate: state.evaluationRate,
    setInitialState: state.setInitialState,
  }))
  const isClassroom = router?.asPath?.includes('/lessons/classroom/') ?? false
  const { request } = useInAppReview()
  const getDefaultOption = (rate: number) => {
    if (rate >= 4) {
      return DISCLOSURE.ALL
    } else if (rate <= 3) {
      return DISCLOSURE.TEAM_AND_TUTOR
    }
  }
  const [detailReviewDesc, setDetailReviewDesc] = useState<string>('')
  const [detailReviewSelect, setDetailReviewSelect] = useState<number | any>(getDefaultOption(evaluationRate)) // 0,1,2
  const wordsLength = detailReviewDesc?.length ?? 0

  const [materialPreference, setMaterialPreference] = useState<number>(0)
  const [materialReviewDesc, setMaterialReviewDesc] = useState<string>('')
  const [networkPreference, setNetworkPreference] = useState<number>(0)
  const [networkReviewDesc, setNetworkReviewDesc] = useState<string>('')

  const moveLessonCompletePage = useFeatureFlag(HACKLE_FLAG_KEY.MOVE_LESSON_COMPLETE_PAGE)

  const radioSetting = {
    name: 'option',
    items: [
      {
        name: t(n_review.step_4.dropdown),
        value: DISCLOSURE.ALL,
      },
      {
        name: t(n_review.step_4.option_1),
        value: DISCLOSURE.TEAM,
      },
      {
        name: t(n_review.step_4.option_2),
        value: DISCLOSURE.TEAM_AND_TUTOR,
      },
    ],
  }

  const getLessonReviewUrl = (response: any, isClassroom: boolean, lessonId: number | string) => {
    const reviewUrl = urls.student.newPortal.lessons.reviews.home + '?update=evaluation'
    if (isClassroom) {
      if (moveLessonCompletePage) {
        return urls.student.newPortal.lessons.complete + '?lesson_id=' + lessonId
      } else {
        return reviewUrl
      }
    }
    if (response?.success) {
      return urls.student.newPortal.lessons.reviewDetail.asFeedback(lessonId) + '&update=evaluation'
    }

    return reviewUrl
  }

  const postDataPayload: PostDataPayload = {
    answer: {
      1: evaluationRate, // 1,2,3,4,5
      2: materialPreference, // 1,2,3
      4: networkPreference, // 1,2,3
    },
    answer_comment: {
      1: detailReviewDesc, //"good lesson!"
      2: materialReviewDesc, //"material Bad!"
      4: networkReviewDesc, //"network Bad!"
    },
    evaluation_keyword_ids: keywordsList,
    public_option: detailReviewSelect, // 0,1,2
    preferred_level: tutorPreference, //1,2,3 //required
  }
  const getFullUrl = useReturnFullUrl()
  const redirectAfterResponse = async (routerPath: string | undefined) => {
    const isClassroom = routerPath?.includes('/lessons/classroom/') ?? false
    const response = await axiosGet(apis.lessons.review.feedback(lessonId, 'score'))
    const url = getLessonReviewUrl(response, isClassroom, lessonId)

    if (isWebview) {
      router.appScreenReset({
        appScreenName: APP_SCREEN_NAME.MAIN_TABS,
        appSendData: { screen: APP_SCREEN_NAME.LESSON_SCREEN },
      })
    } else {
      router.push(url, {
        appScreenName: APP_SCREEN_NAME.LESSON_SCREEN,
        appSendData: { url: getFullUrl(urls.webview.lessons.index + '?update=evaluation') },
      })
    }
    onClose()
    isRookie ? toast(t(s_review.toast_pointback)) : toast(t(s_review.toast))
  }

  const handleEndEvaluation = async (
    wordsLength: number,
    routerPath: string | undefined,
    lessonId: number,
    payload: PostDataPayload
  ) => {
    if (wordsLength > 0 && wordsLength < 20) {
      confirmPopup.show({
        id: 'wording-error-popup',
        body: t(s_review.desc_error),
        leftBtn: false,
        rightBtnLabel: t(s_review.btn_9),
      })
      return
    }

    setLoading(true)
    if (postDataPayload.answer[1] === 5) {
      isWebview && request()
    }
    try {
      sendClickEventToGTM('평가 완료')

      const response = await axiosPost(apis.evaluation.post(lessonId), payload)
      if (response.success) {
        redirectAfterResponse(routerPath)
      }
    } catch (error) {
      console.log(error, 'error')
    } finally {
      refetch && refetch()
      onClose()
      setInitialState()
      setLoading(false)
    }
  }

  const handleLaterButton = () => {
    onClose()
    sendClickEventToGTM('나중에 하기')

    if (isClassroom) {
      if (moveLessonCompletePage) {
        router.push(urls.student.newPortal.lessons.complete + '?lesson_id=' + lessonId)
      } else {
        router.push(urls.student.newPortal.lessons.reviews.home)
      }
    }
  }

  //in handleRookieTutorPointBack() 신규 튜터 포인트백
  const submitRookieTutorEvaluation = async (routerPath: string, lessonId: number, payload: PostDataPayload) => {
    setLoading(true)

    try {
      sendClickEventToGTM('평가 완료')

      const response = await axiosPost(apis.evaluation.post(lessonId), payload)
      if (response.success) {
        redirectAfterResponse(routerPath)
      }
    } catch (error) {
      console.log(error, 'error')
    } finally {
      refetch && refetch()
      onClose()
      setInitialState()
      toast(t(s_review.toast_pointback))
      setLoading(false)
    }
  }

  // 신규 튜터 포인트백 제출시
  const handleRookieTutorPointBack = (
    wordsLength: number,
    routerPath: string,
    lessonId: number,
    payload: PostDataPayload,
    pointBackAmount?: number
  ) => {
    if (wordsLength < 100) {
      confirmPopup.show({
        id: 'wording-error-popup',
        title: t(s_review.title_pointback),
        body: t(s_review.desc_pointback(pointBackAmount ? pointBackAmount.toLocaleString() : '[undefined]')),
        leftBtnLabel: t(s_review.btn_10),
        onLeftBtnClick: () => submitRookieTutorEvaluation(routerPath, lessonId, payload),
        rightBtnLabel: t(s_review.btn_8),
      })
      return
    } else {
      submitRookieTutorEvaluation(routerPath, lessonId, payload)
    }
  }

  const handleSubmitButtonClick = () => {
    if (!isRookie) {
      return handleEndEvaluation(wordsLength, router.asPath, lessonId, postDataPayload)
    }

    if (wordsLength >= 100) {
      return handleEndEvaluation(wordsLength, router.asPath, lessonId, postDataPayload)
    } else if (wordsLength === 0 || (wordsLength >= 20 && wordsLength < 100)) {
      return handleRookieTutorPointBack(wordsLength, router.asPath, lessonId, postDataPayload, pointBackAmount)
    } else {
      return handleEndEvaluation(wordsLength, router.asPath, lessonId, postDataPayload)
    }
  }

  return (
    <div className="flex h-[calc(100vh_-_100px)] flex-col justify-between sm:h-full">
      <div>
        <div className="Headline2-24Bd keep-all ">{t(n_review.step_4.title(tutorData.name))}</div>
        <div className="mt-[24px] flex flex-row items-center justify-between">
          <div className="Body1-16Bd mb-[8px]">{t(n_review.step_4.title_review)}</div>
          <Popover as="div" className="relative cursor-pointer self-center">
            {({ open }) => (
              <>
                <Popover.Button className={'lift Caption-12Bd flex cursor-pointer'}>
                  <div className={'flex flex-row items-center gap-[2px] text-black'}>
                    <div className="pt-[2px]">{radioSetting.items[detailReviewSelect].name}</div>
                    {open ? <QIcon_chevron_up width={12} height={12} /> : <QIcon_chevron_down width={12} height={12} />}
                  </div>
                </Popover.Button>
                <Transition
                  show={open}
                  as={Fragment}
                  enter="transition ease-out duration-200"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Popover.Panel
                    className="Body2-14Md absolute z-20 p-[8px] shadow-lg"
                    style={{
                      bottom: '-150px',
                      right: '2px',
                      backgroundColor: 'white',
                      width: '160px',
                      minWidth: '0',
                      boxSizing: 'border-box',
                      borderRadius: '8px',
                      boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.15)',
                    }}
                  >
                    {({ close }) => (
                      <>
                        {radioSetting.items.map((setting, settingIdx) => {
                          return (
                            <div
                              key={settingIdx}
                              className={classNames(
                                setting.value === detailReviewSelect && 'bg-purple-50 text-purple-500',
                                'rounded-[4px] py-[11px] px-[12px] hover:bg-purple-50'
                              )}
                              onClick={() => {
                                setDetailReviewSelect(setting.value)
                                close()
                              }}
                            >
                              {setting.name}
                            </div>
                          )
                        })}
                      </>
                    )}
                  </Popover.Panel>
                </Transition>
              </>
            )}
          </Popover>
        </div>

        {isRookie && (
          <p className="text-h6 font-medium text-[#ff6712]">
            {t(s_review.info_pointback(pointBackAmount ? pointBackAmount.toLocaleString() : '[undefined]'))}
          </p>
        )}
        <div className="relative mt-[8px]">
          <TextareaAutosize
            style={{ fontSize: 14, borderRadius: '5px' }}
            className="Body2-14Md h-[98px] w-full resize-none border-0 bg-gray-50 px-[16px] pt-[8px] placeholder:text-gray-400 focus:outline-none focus:ring-0"
            onChange={(e) => {
              setDetailReviewDesc(e.target.value)
            }}
            value={detailReviewDesc}
            minRows={3}
            maxRows={3}
            placeholder={t(s_review.placeholder_textreview)}
            onBlur={() => console.log('test blur')}
          />
          <div className="Caption-12Md flex flex-row justify-end">
            <div>{wordsLength}</div> <div className="ml-[4px]  text-gray-500">{t(s_review.word_count)}</div>
          </div>
        </div>
      </div>
      <div className="mt-[9px]">
        <div className="flex flex-col items-start">
          <div className="Body1-16Bd mb-[8px] flex font-medium">{t(n_review.step_4.title_material)}</div>
          <div className="flex w-full flex-row items-center justify-between">
            <div className="Body2-14Md text-gray-500">{course.title}</div>
            <SelectPreferenceSection
              list={preferList}
              selected={materialPreference}
              setSelected={setMaterialPreference}
              wrapperClx="flex flex-row gap-[8px]"
              setHover={null}
            />
          </div>
        </div>
        <div className="">
          {materialPreference !== 0 && (
            <TextareaAutosize
              style={{ fontSize: 14, borderRadius: '5px' }}
              className="Body2-14Md mt-[8px] w-full resize-none border-0 border-gray-300 bg-gray-50 px-[16px] py-[14px] placeholder:text-gray-400 focus:outline-none focus:ring-0"
              onChange={(e) => {
                setMaterialReviewDesc(e.target.value)
              }}
              value={materialReviewDesc}
              minRows={1}
              maxRows={1}
              placeholder={t(n_review.step_4.placeholder_comment)}
            />
          )}
        </div>
      </div>
      <div className="flex h-full flex-col justify-between">
        <div className="mt-[28px]">
          <div className="flex flex-col">
            <div className="Body1-16Bd">{t(n_review.step_4.title_network)}</div>
            <div className="mt-8px] flex flex-row items-center justify-between">
              <div className="">{t(n_review.step_4.caption_network)}</div>
              <SelectPreferenceSection
                list={preferList}
                selected={networkPreference}
                setSelected={setNetworkPreference}
                wrapperClx="flex flex-row gap-[8px]"
                setHover={null}
              />
            </div>
          </div>
          <div className="">
            {networkPreference !== 0 && (
              <TextareaAutosize
                style={{ fontSize: 14, borderRadius: '5px' }}
                className="Body2-14Md mt-[8px] w-full resize-none border-0 border-gray-300 bg-gray-50 px-[16px] py-[14px] placeholder:text-gray-400 focus:outline-none focus:ring-0"
                onChange={(e) => {
                  setNetworkReviewDesc(e.target.value)
                }}
                value={networkReviewDesc}
                minRows={1}
                maxRows={1}
                placeholder={t(n_review.step_4.placeholder_comment)}
              />
            )}
          </div>
        </div>

        <div className="mt-[24px] flex justify-end gap-[8px]">
          <div className="hidden sm:block sm:h-[46px] sm:w-[112px]">
            <DSButton size={BUTTON_SIZE.MD} type={BUTTON_TYPE.OUTLINE} onClick={() => handleLaterButton()}>
              <DSButton.Label>{t(n_common.btn_leave)}</DSButton.Label>
            </DSButton>
          </div>
          <div className="mb-[34px] w-full sm:mb-[0px] sm:h-[46px] sm:w-[112px]">
            <DSButton
              size={BUTTON_SIZE.MD}
              type={BUTTON_TYPE.SOLID}
              onClick={() => handleSubmitButtonClick()}
              className="border-1 border-transparent"
            >
              <DSButton.Label> {loading ? <LoadingSpinner sm show gray400 /> : t(s_review.btn_4)}</DSButton.Label>
            </DSButton>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EvaluationStep4
